(() => {
  class ProductDetail extends HTMLElement {
    constructor() {
      super();
    }

    swatchUnsubscriber = undefined;
    variantChangeUnsubscriber = undefined;

    connectedCallback() {
      this.quickviewEl = this.closest('quick-view');
      this.isQuickview = this.quickviewEl !== null;
      this.pdpEls();
      this.pdpSettings();
      this.pdpListeners();
      this.loadRecsEarly();
      this.setRecentlyViewed();

      this.swatchUnsubscriber = window.subscribe(PUB_SUB_EVENTS.swatchChange, event => {
        if (event.source !== 'product-detail') return;
        const isQuickviewEvent = event.isQuickview || false;
        this.pdpEl = event.pdpEl || this.pdpEl;
        this.blockEls = this.pdpEl.querySelectorAll(`${this.infoElSelector} [data-block-type]`);
        this.infoEl = this.pdpEl.querySelector(this.infoElSelector);
        this.elsToUpdate = Array.from(this.blockEls).map(el => `[data-block-type="${el.dataset.blockType}"]`);
        this.elsToUpdate.push('[data-pdp-gallery]');
        if (event.el) this.clickedSwatchEl = event.el;
        if (this.pdpEl.querySelector('[data-product-media-zoom-modal]')) this.elsToUpdate.push('[data-product-media-zoom-modal]');
        this.currentVariant = event.currentVariant;
        this.swatchVariants = event.data.variants;
        this.optionValues = event.optionValues;
        this.swatchColorName = event.data.name;
        this.onChange(event.url, isQuickviewEvent);
      });

      this.variantChangeUnsubscriber = window.subscribe(PUB_SUB_EVENTS.variantChange, event => {
        if (event.sectionId !== this.sectionId && !this.isQuickview) return;
        this.currentVariant = event.currentVariant;
        this.onVariantChange();
      });
    }

    disconnectedCallback() {
      const unsubscribers = [
        this.swatchUnsubscriber,
        this.variantChangeUnsubscriber,
      ];

      unsubscribers.forEach(unsubscriber => unsubscriber && unsubscriber());
    }

    loadRecsEarly() {
      // Instead of waiting for recs to get scrolled into view, start loading when page is idle
      const pdpHasRecs = document.querySelector('product-recommendations');
      if (!pdpHasRecs || typeof window.requestIdleCallback !== 'function') return;

      window.requestIdleCallback(deadline => {
        while (deadline.timeRemaining() > 0) {
          lazyLoadScript({
            src: window.assetUrls['product-recommendations.js']
          });
        }
      }, { timeout: 5000 });
    }

    setRecentlyViewed() {
      const handle = this.dataset.productHandle;

      if (localStorage.getItem('product_recently_viewed')) {
        const handles = JSON.parse(localStorage.getItem('product_recently_viewed'));
        const index = handles.indexOf(handle);
        if (index !== -1) {
          handles.splice(index, 1);
          handles.unshift(handle);
        } else {
          handles.push(handle);
        }
        localStorage.setItem('product_recently_viewed', JSON.stringify(handles));
      } else {
        localStorage.setItem('product_recently_viewed', JSON.stringify([handle]));
      }
    }

    pdpEls() {
      this.pdpEl = this;
      this.sectionId = this.closest('product-detail').dataset.sectionId;
      this.infoElSelector = '[data-pdp-info]';

      this.blockEls = this.querySelectorAll(`${this.infoElSelector} [data-block-type]`);
      this.infoEl = this.querySelector(this.infoElSelector);
      this.lowStockEl = this.querySelector('[data-low-stock-threshold]');
      this.storePickupEl = this.querySelector('[data-store-pickup-info]');
      this.pageSectionEls = this.closest('main')?.querySelectorAll('.shopify-section');
    }

    pdpSettings() {
      this.elsToUpdate = Array.from(this.blockEls).map(el => `[data-block-type="${el.dataset.blockType}"]`);
      this.elsToUpdate.push('[data-pdp-gallery]');
      if (this.pdpEl.querySelector('[data-product-media-zoom-modal]')) this.elsToUpdate.push('[data-product-media-zoom-modal]');
      this.inventoryData = JSON.parse(this.querySelector('[data-json="product-variants-inventory"]').textContent);
      this.pageSectionIds = [];
      if (!this.isQuickview) this.pageSectionEls.forEach(section => this.pageSectionIds.push(section.id.split('shopify-section-')[1]));

      const installmentsEl = this.querySelector('#product-form-installment');
      if (installmentsEl) {
        this.productFormInstallment = { el: installmentsEl };
        this.productFormInstallment.id = this.productFormInstallment.el.querySelector('[name="id"]');
        this.productFormInstallment.productId = this.productFormInstallment.el.querySelector('[name="product-id"]');
      }

      /* store page sections in chunks of 5 (sections rendering api limit per call) */
      this.apiSectionLimit = 5;
      this.sectionEls = [];
      this.sectionIds = [];
      document.querySelectorAll('main > .shopify-section:not(.shopify-section--product)').forEach(el => {
        if (!el.classList.contains('shopify-section--yotpo-reviews')) {
          this.sectionEls.push(el);
          this.sectionIds.push(el.id.split('shopify-section-')[1]);
        }
      });
      this.sectionElsChunks = [];
      this.sectionIdsChunks = [];
      while (this.sectionIds.length > 0) {
        this.sectionElsChunks.push(this.sectionEls.splice(0, this.apiSectionLimit));
        this.sectionIdsChunks.push(this.sectionIds.splice(0, this.apiSectionLimit));
      }
    }

    pdpListeners() {
      if (this.isQuickview) return;

      const onHistoryChange = () => {
        const updateActiveSwatch = true;
        this.updateContent({ url: location.href, updateActiveSwatch, isQuickview: false });
      };

      window.addEventListener('popstate', onHistoryChange);
    }

    onChange(url, isQuickviewEvent = false) {
      const notQuickview = !this.isQuickview && !isQuickviewEvent;

      this.updateContent({ url, updateActiveSwatch: true, isQuickview: !notQuickview });
    }

    onVariantChange() {
      window.publish(PUB_SUB_EVENTS.stickyFormUpdate, {
        currentVariant: this.currentVariant,
      });

      const params = new URLSearchParams(window.location.search);

      params.set('variant', this.currentVariant.id);

      const url = `${window.location.pathname}?${params.toString()}`;
      this.updateUrl(url)

      if (this.lowStockEl) {
        const inventory = this.inventoryData[this.currentVariant.id];
        const lowStockThreshold = parseInt(this.lowStockEl.dataset.lowStockThreshold);
        if (!lowStockThreshold) return;

        if (inventory > lowStockThreshold || inventory === 0 || !this.currentVariant || this.currentVariant.available === false) {
          this.lowStockEl.classList.add('hide');
        } else {
          this.lowStockEl.classList.remove('hide');
        }
      }

      if (this.storePickupEl) {
        this.updateStorePickup()
      }
    }

    updateActiveSwatch(url) {
      const activeSwatchEl = this.infoEl.querySelector(`.product-swatches__item--active`);
      if (activeSwatchEl) activeSwatchEl.classList.remove('product-swatches__item--active');
      const newActiveSwatchEl = this.infoEl.querySelector(`[data-swatch-item="${url.split('/products/')[1].split('?')[0]}"]`);
      newActiveSwatchEl && newActiveSwatchEl.classList.add('product-swatches__item--active');
    }

    updateStorePickup() {
      fetch(`${Shopify.routes.root}products/${this.dataset.productHandle}?variant=${this.currentVariant.id}&section_id=product-pickup-availability`)
        .then(response => response.text())
        .then(responseText => {
          const parsedHtml = new DOMParser().parseFromString(responseText, 'text/html');
          const newPickupInfoEl = parsedHtml.querySelector('[data-store-pickup-info]');
          const currentPickupInfoEl = this.querySelector('[data-store-pickup-info]');
          if (newPickupInfoEl && currentPickupInfoEl) {
            currentPickupInfoEl.innerHTML = newPickupInfoEl.innerHTML;
          }
        })
        .catch(e => console.error(e));
    }

    updateContent({ url, updateActiveSwatch = false, isQuickview = false }) {
      // Capture whether a size is checked RIGHT NOW, before any re-render.
      // This is the ground truth for all three contexts (PDP, QV, QV-on-PDP).
      const variantRadios = this.querySelector('variant-radios');
      const hasSizeSelected = variantRadios
        ? variantRadios.querySelector('fieldset[data-product-option]:not(.product-swatches) input:checked') !== null
        : false;

      // Store so renderMainSections can re-apply uncheck after its async re-render.
      this.hasSizeSelected = hasSizeSelected;

      const urlSplit = url.split('?');
      const params = new URLSearchParams(urlSplit[1]);
      params.set('section_id', this.sectionId);
      if (this.optionValues) params.set('option_values', this.optionValues.join(','));
      fetch(`${urlSplit[0]}?${params.toString()}`)
        .then(response => response.text())
        .then(responseText => {
          this.updateDetails(responseText, hasSizeSelected);
          if (!isQuickview) this.updateUrl(url, hasSizeSelected);
          if (updateActiveSwatch) this.updateActiveSwatch(url);
          this.clickedSwatchEl?.classList.remove('loading');

          requestAnimationFrame(() => {
            if (!isQuickview) this.updateMainSections(url);
            this.updateBuyForm(responseText, hasSizeSelected);

            if (!hasSizeSelected) {
              this.uncheckSizeInputs();
            }
          });
        })
        .catch((e) => {
          console.error(e);
        });
    }

    updateDetails(html, hasSizeSelected = true) {
      this.elsToUpdate.forEach(selector => {
        const el = this.pdpEl.querySelector(selector) || this.querySelector(selector);
        const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
        const parsedEl = parsedHtml.querySelector(selector);
        if (!(el && parsedEl)) return;
        el.innerHTML = parsedEl.innerHTML;
      })

      // If no size was selected, clear the option label text (e.g. "Size: S")
      // for all non-swatch fieldsets so the legend shows "Size:" with no value
      if (!hasSizeSelected) {
        this.querySelectorAll('variant-radios fieldset[data-product-option]:not(.product-swatches)').forEach(fieldset => {
          const optionIndex = fieldset.dataset.productOption;
          const labelEl = this.querySelector(`[data-product-option-active="${optionIndex}"]`);
          if (labelEl) labelEl.innerText = '';
        });
      }

      this.quickviewEl = this.pdpEl.closest('quick-view');
      requestAnimationFrame(() => {
        this.quickviewEl?.update();
        // Run after update() re-renders the quickview panel so any
        // Shopify-default checked state is cleared in all contexts.
        if (!hasSizeSelected) {
          this.uncheckSizeInputs();
        }
        window.swipers();
        window.inView();
      });
    }

    uncheckSizeInputs() {
      const variantRadios = this.querySelector('variant-radios');
      if (!variantRadios) return;
      variantRadios.querySelectorAll('fieldset[data-product-option]:not(.product-swatches)').forEach(fieldset => {
        // Uncheck all size inputs
        fieldset.querySelectorAll('input').forEach(input => {
          input.checked = false;
          input.removeAttribute('checked');
        });
        // Clear the size label text (e.g. "Size: S")
        const labelEl = this.querySelector(`[data-product-option-active="${fieldset.dataset.productOption}"]`);
        if (labelEl) labelEl.innerText = '';
      });
    }

    renderMainSections(sectionsHtml, url, chunkIndex) {
      this.sectionElsChunks[chunkIndex].forEach((section, index) => {
        const id = this.sectionIdsChunks[chunkIndex][index];
        const div = document.createElement('div');
        const sectionHtml = sectionsHtml[id];
        if (!sectionHtml) return;

        /* updat section's html */
        div.innerHTML = sectionsHtml[id];
        const html = div.querySelector(`#shopify-section-${id}`).innerHTML;
        if (id === 'sticky-form') {
          if (section.querySelector('sticky-form') && div.querySelector(`sticky-form`)) {
            section.querySelector('sticky-form').innerHTML = div.querySelector(`sticky-form`).innerHTML;
            setTimeout(() => {
              window.publish(PUB_SUB_EVENTS.stickyFormStart, {});
            }, 500);
          }
        } else {
          section.innerHTML = html;
        }

        /* update page title */
        if (index > 0) return;
        const pageTitle = div.querySelector('[data-meta-title]');
        if (pageTitle) document.title = pageTitle.dataset.metaTitle;
      });

      window.swiperLoaded = false;
      requestAnimationFrame(() => {
        window.swipers();
        window.inView();

        // Re-apply uncheck after renderMainSections swaps in fresh HTML
        // which may include Shopify's default checked size attribute.
        if (!this.hasSizeSelected) {
          this.uncheckSizeInputs();
        }
      });
    }

    updateMainSections(url) {
      if (this.isQuickview) return;

      const urlSplit = url.split('?');
      const params = new URLSearchParams(urlSplit[1]);
      if (this.optionValues) params.set('option_values', this.optionValues.join(','));

      this.sectionIdsChunks.forEach((ids, index) => {
        params.append('sections', ids.join(','));

        fetch(`${urlSplit[0]}?${params.toString().replaceAll('%2C', ',')}`).then((response) => {
          /* make sure the page loads without js in case of any errors (429, etc...) */
          if (!response.ok) {
            window.location = url;
            return null;
          }
          return response.text();
        }).then(response => {
          if (!response) return;
          this.renderMainSections(JSON.parse(response), url, index);
        });
      });
    }

    updateUrl(url, hasSizeSelected = true) {
      if (this.isQuickview) return;

      const urlSplit = url.split('?');
      const params = new URLSearchParams(urlSplit[1]);

      if (hasSizeSelected) {
        // A size is selected — include the variant ID in the URL
        const vId = this.currentVariant ? this.currentVariant.id : this.querySelector('[name="id"]').value;
        if (vId) params.set('variant', vId);
      } else {
        // No size selected — just update the URL to the new product, no variant param
        params.delete('variant');
      }

      url = `${urlSplit[0]}?${params.toString()}`;
      window.history.pushState({}, '', url);
    }

    updateBuyForm(html, hasSizeSelected = true) {
      if (!html) {
        console.error('Unable to update product form');
        return;
      }

      // Fetched product data
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      const parsedForm = parsedHtml.querySelector('product-form');
      const parsedFormInputEl = parsedForm.querySelector(`#product-form-${this.dataset.sectionId} input[name="id"]`);
      const parsedAtcBtn = parsedForm.querySelector('.pdp__submit');
      const parsedBisBtn = parsedForm.querySelector('.product__bis');

      // Existing DOM els
      const productForm = this.querySelector('product-form');
      const formInputEl = this.querySelector(`#product-form-${this.dataset.sectionId} input[name="id"]`);
      const atcBtn = this.querySelector('.pdp__submit');
      const bisBtn = this.querySelector('.product__bis');

      if (hasSizeSelected) {
        // Update hidden form input with variant ID
        const id = parsedFormInputEl?.value || null;
        if (formInputEl) formInputEl.value = id;

        // Update ATC and BIS buttons
        if (atcBtn && parsedAtcBtn) atcBtn.replaceWith(parsedAtcBtn);
        if (bisBtn && parsedBisBtn) bisBtn.replaceWith(parsedBisBtn);

        if (!parsedForm.classList.contains('bis-active')) {
          bisBtn?.classList.add('hide');
        } else {
          productForm?.classList.add('bis-active');
        }
      }
      // When no size is selected, leave the form input, ATC button, and BIS
      // button as-is — the current state already reflects "no selection".

      // Update product info in BIS modal
      const parsedBisProductInfo = parsedHtml.querySelector('[data-bis-product-info]');
      const bisProductInfoEl = this.querySelector('[data-bis-product-info]');
      if (parsedBisProductInfo && bisProductInfoEl) bisProductInfoEl.innerHTML = parsedBisProductInfo.innerHTML;
    }
  }

  if (document.documentElement.dataset.templateName.includes('product')) {
    document.addEventListener('DOMContentLoaded', () => {
      if (!customElements.get('product-detail')) {
        customElements.define('product-detail', ProductDetail);
      }
    });
  } else {
    if (!customElements.get('product-detail')) {
      customElements.define('product-detail', ProductDetail);
    }
  }
})();
